import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CContainer,
  CCard,
  CCardHeader,
  CCardBody,
  CButton,
  CSpinner,
  COffcanvas,
  COffcanvasBody,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilSettings, cilMediaPlay } from "@coreui/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ru from "date-fns/locale/ru";
import kk from "date-fns/locale/kk";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";

import CameraModals from "./components/CameraModals";
import CameraTabs from "./components/CameraTabs";
import CameraHeader from "./components/CameraHeader";
import CameraFilterPanel from "./components/CameraFilterPanel";
import CameraVideoPlayer from "./components/CameraVideoPlayer";
import CameraArchiveList from "./components/CameraArchiveList";
import CameraExtraModals from "./components/CameraExtraModals";
import TruckAnalyticsDashboard from "./components/TruckAnalyticsDashboard";
import TruckTimelineSection from "./components/TruckTimelineSection";
import TripProgressStrip from "./components/TripProgressStrip";
import TruckMap from "./components/TruckMap";

import {
  formatDuration,
  getDeviceOffsetMin,
  getFakeLocalDate,
  fromFakeLocalToUtcIso,
} from "./utils/timeUtils";
import {
  ACT,
  TRUCK_CLASS_META,
  normalizeClassId,
  tripColor,
  createStore,
  buildTrackIndex,
  positionAt,
} from "./utils/truckActivity";
import "./CameraDetailPage.css";

registerLocale("ru", ru);
registerLocale("kk", kk);

const VIDEO_FPS = 24.0;

// Щели между сегментами шкалы короче этого - стыки архивов, а не дыры в данных:
// серую вставку «Нет данных» для них не рисуем
const SMALL_GAP_MS = 2000;

// После заказанной перемотки плеер еще какое-то время отдает прогресс со старой позиции.
// Пока окно не истекло, прогресс игнорируем, иначе маркер отскакивает назад (как в ImuViewerPage).
const SEEK_SETTLE_MS = 400;

const VIDEO_LAYOUT_STORAGE_KEY = "truckVideoModalLayout";
// Окно ниже этого по высоте - «невысокий экран»: шкала и плеер в окне видео компактные,
// отступы меньше. На высоком экране шкала полного размера, как на странице.
const COMPACT_MAX_HEIGHT_PX = 950;

// Раскладка окна «Видео, карта и шкала работы».
// Широкий экран (от 1200 px):
//   bottom - видео | карта в ряд, ряд занимает всё, что осталось над шкалой (не меньше 300 px);
//   side   - видео слева во всю высоту, справа карта и под ней шкала; если справа не хватает
//            места, прокручивается только правая колонка, видео стоит на месте.
// Узкий экран - всё колонкой: видео (16:9 по ширине, не выше 60vh), карта 360 px, шкала;
// окно прокручивается. Ряду не разрешено сжиматься до нуля: если не хватает высоты,
// прокручивается окно, а не схлопываются видео и карта.
const VIDEO_MODAL_CSS = `
.truck-modal-main { display: flex; flex-direction: column; gap: 0.75rem; flex-shrink: 0; }
.truck-modal-video { height: 36vh; min-height: 230px; min-width: 0; flex-shrink: 0; }
.truck-modal-side { display: flex; flex-direction: column; min-width: 0; gap: 0.5rem; flex-shrink: 0; }
.truck-modal-mapbox { height: 360px; min-height: 340px; display: flex; flex-direction: column; flex-shrink: 0; }
.truck-modal-mapbox .archive-match-height { height: 100% !important; max-height: 100% !important; }
.truck-modal-timeline > .row { margin-bottom: 0 !important; }

@media (min-width: 1200px) {
  /* справа в заголовке виден выбор раскладки: крестик ставим вплотную к нему */
  .truck-video-modal .modal-header .btn-close { margin-left: 0; }
  .truck-modal-main { flex-direction: row; }
  .truck-modal-video { height: auto; min-height: 0; }
  .truck-modal-mapbox { height: auto; flex: 1 1 auto; min-height: 180px; }

  .truck-layout-bottom.truck-modal-main { flex: 1 1 0; min-height: 300px; }
  .truck-layout-bottom .truck-modal-video,
  .truck-layout-bottom .truck-modal-side { flex: 1 1 0; }

  .truck-layout-side.truck-modal-main { flex: 1 1 0; min-height: 360px; }
  .truck-layout-side .truck-modal-video { flex: 0 0 auto; width: clamp(320px, 36%, 760px); }
  .truck-layout-side .truck-modal-side { flex: 1 1 0; min-height: 0; overflow-y: auto; padding-right: 2px; }
  .truck-layout-side .truck-modal-side > * { flex-shrink: 0; }
  .truck-layout-side .truck-modal-mapbox { flex: 1 0 auto; height: 180px; }
}

/* невысокий экран: тоньше шапка окна и отступы */
@media (max-height: ${COMPACT_MAX_HEIGHT_PX}px) {
  .truck-video-modal .modal-header { padding: 0.4rem 1rem; }
  .truck-video-modal .modal-title { font-size: 1.05rem; }
  .truck-video-modal .modal-body { padding: 0.5rem 0.75rem; gap: 0.5rem !important; }
  .truck-modal-main { gap: 0.5rem; }
  .truck-modal-mapbox > .card-body { padding: 0.375rem !important; }
}
`;

// true, пока окно браузера ниже COMPACT_MAX_HEIGHT_PX (следит за изменением размера)
const useLowViewport = () => {
  const query = `(max-height: ${COMPACT_MAX_HEIGHT_PX}px)`;
  const [low, setLow] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const update = () => setLow(mql.matches);
    update();
    if (mql.addEventListener) mql.addEventListener("change", update);
    else mql.addListener(update);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", update);
      else mql.removeListener(update);
    };
  }, [query]);
  return low;
};

// frames_mapping: [{ f: номер кадра, fake: время кадра в fake-local мс }], по возрастанию f.
// Время кадра видео: последний кадр с f <= номера текущего кадра.
const fakeMsAtFrame = (mapping, frame) => {
  let lo = 0;
  let hi = mapping.length - 1;
  let found = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (mapping[mid].f <= frame) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return mapping[found].fake;
};

// Номер кадра для момента времени: первый кадр, снятый не раньше этого момента
const frameAtFakeMs = (mapping, fakeMs) => {
  let lo = 0;
  let hi = mapping.length - 1;
  let found = mapping.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (mapping[mid].fake >= fakeMs) {
      found = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  return mapping[found].f;
};

const TruckDetailPage = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language ? i18n.language.split("-")[0] : "ru";
  const { cameraId } = useParams();
  const isSuperuser = localStorage.getItem("is_superuser") === "true";
  const isStaff = localStorage.getItem("is_staff") === "true" || isSuperuser;

  const [device, setDevice] = useState(null);
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [volumeMode, setVolumeMode] = useState("nominal");

  const [offcanvasVisible, setOffcanvasVisible] = useState(false);
  const [videoModalVisible, setVideoModalVisible] = useState(false);

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [currentFolderStartTime, setCurrentFolderStartTime] = useState(null);
  const [crossVideo, setCrossVideo] = useState(null);

  const [framesMapping, setFramesMapping] = useState([]);
  const framesMappingRef = useRef([]);
  useEffect(() => {
    framesMappingRef.current = framesMapping;
  }, [framesMapping]);

  const [analyticsData, setAnalyticsData] = useState(null);
  const [trajectoryData, setTrajectoryData] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [analyticsCooldown, setAnalyticsCooldown] = useState(0);

  const [datesInitialized, setDatesInitialized] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterMode, setFilterMode] = useState("custom");
  const [datePreset, setDatePreset] = useState("today");
  const [baseDate, setBaseDate] = useState(null);
  const [selectedShiftId, setSelectedShiftId] = useState("");

  // Пересчет за период: начало и конец (у экскаватора - только «с даты»)
  const [recalcStartDate, setRecalcStartDate] = useState(null);
  const [recalcEndDate, setRecalcEndDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [recalcModalVisible, setRecalcModalVisible] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  const [seekToTime, setSeekToTime] = useState(null);
  const [seekTriggerId, setSeekTriggerId] = useState(0);
  const customPlayerRef = useRef(null);
  const lastUserSeekAtRef = useRef(0);

  // Масштаб таймлайна и время кадра видео меняются десятки раз в секунду - держим их
  // во внешних хранилищах, а не в состоянии: иначе каждое изменение перерисовывало бы
  // всю страницу с графиками. Таймлайны и карта подписаны на них сами.
  const viewStore = useMemo(() => createStore(null), []);
  const playheadStore = useMemo(() => createStore(null), []);
  // Вид карты пользователя (центр, масштаб, тип тайлов): общий для карты на странице и в окне видео
  const mapViewRef = useRef({});
  // Рейс, трек которого показан на карте (null - весь период)
  const [mapTripId, setMapTripId] = useState(null);
  const mapTripIdRef = useRef(null);
  mapTripIdRef.current = mapTripId;
  // «Рейс по видео»: карта сама переключается на рейс, в котором находится текущий кадр
  const [autoTripFromVideo, setAutoTripFromVideo] = useState(true);
  const autoTripRef = useRef(true);
  autoTripRef.current = autoTripFromVideo;
  // Правая колонка окна видео: карта или список архивов
  const [videoSideTab, setVideoSideTab] = useState("map");
  // Раскладка окна видео: "bottom" - видео и карта рядом, шкала снизу;
  // "side" - видео слева во всю высоту, карта и шкала справа (для вертикального видео).
  // Запоминается в браузере пользователя
  const [videoLayout, setVideoLayout] = useState(() => {
    try {
      return localStorage.getItem(VIDEO_LAYOUT_STORAGE_KEY) === "side" ? "side" : "bottom";
    } catch {
      return "bottom";
    }
  });
  const changeVideoLayout = (layout) => {
    setVideoLayout(layout);
    try {
      localStorage.setItem(VIDEO_LAYOUT_STORAGE_KEY, layout);
    } catch {
      // приватный режим или запрет хранилища - раскладка просто не запомнится
    }
  };
  // Компактные шкала и плеер в окне видео - только на невысоком экране
  const lowViewport = useLowViewport();

  const [liveData, setLiveData] = useState(null);
  const [liveIntervalId, setLiveIntervalId] = useState(null);
  const [liveImageModalVisible, setLiveImageModalVisible] = useState(false);

  // Нужны handleTimeDoubleClick и onClose модалки видеоархива (ветка заездов не используется)
  const [sessionsModalVisible, setSessionsModalVisible] = useState(false);
  const [returnToSessionsModal, setReturnToSessionsModal] = useState(false);

  const [schedule, setSchedule] = useState({
    shift_start: "00:00",
    shift_end: "23:59",
    work_days: 127,
    timezone_offset: 0,
    downtime_threshold_sec: 300,
    has_multiple_shifts: false,
    realtime_stream_enabled: false,
    include_breaks_in_stats: true,
    bucket_volume: 0,
    fuel_tank_volume: 500,
  });

  const [shifts, setShifts] = useState([]);
  const [newShift, setNewShift] = useState({
    name: t("camera_settings.default_shift_day", "Дневная"),
    start_time: "08:00",
    end_time: "20:00",
  });
  const [exceptions, setExceptions] = useState([]);
  const [newException, setNewException] = useState({
    startDate: new Date(),
    endDate: new Date(),
    is_working: false,
    is_maintenance: false,
    description: "",
    custom_shift_start: "",
    custom_shift_end: "",
  });
  const [breaks, setBreaks] = useState([]);
  const [newBreak, setNewBreak] = useState({
    start_time: "12:00",
    end_time: "13:00",
    description: t("camera_settings.default_break", "Обед"),
  });
  const [materials, setMaterials] = useState([]);
  const [newMaterial, setNewMaterial] = useState({
    name: t("camera_materials.default_material", "Грунт"),
    is_default: false,
    startDate: new Date(),
    endDate: new Date(),
    bulk_density: 1650,
    true_density: 2600,
    fill_factor: 1.0,
    loosening_factor: 1.58,
  });

  const [savingSchedule, setSavingSchedule] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");

  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadReport, setUploadReport] = useState(null);

  const [formatModalVisible, setFormatModalVisible] = useState(false);
  const [formatMode, setFormatMode] = useState("all");
  const [formatDate, setFormatDate] = useState(null);
  const [formatConfirmation, setFormatConfirmation] = useState("");
  const [formatting, setFormatting] = useState(false);

  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportStart, setExportStart] = useState(new Date());
  const [exportEnd, setExportEnd] = useState(new Date());
  const [isExporting, setIsExporting] = useState(false);

  // Классы активностей самосвала (21-26) + служебные -1, -2, -3
  const classIdToMeta = useMemo(() => TRUCK_CLASS_META, []);

  useEffect(() => {
    if (analyticsCooldown > 0) {
      const timer = setTimeout(() => setAnalyticsCooldown((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [analyticsCooldown]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const loadConfig = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/`, { headers });
        setDevice(res.data);
        const offset = res.data.timezone_offset || 0;
        setSchedule({
          shift_start: res.data.shift_start.substring(0, 5),
          shift_end: res.data.shift_end.substring(0, 5),
          work_days: res.data.work_days,
          timezone_offset: offset,
          downtime_threshold_sec: res.data.downtime_threshold_sec || 300,
          has_multiple_shifts: res.data.has_multiple_shifts || false,
          realtime_stream_enabled: res.data.realtime_stream_enabled || false,
          include_breaks_in_stats:
            res.data.include_breaks_in_stats !== undefined ? res.data.include_breaks_in_stats : true,
          bucket_volume: res.data.bucket_volume ?? 0,
          fuel_tank_volume: res.data.fuel_tank_volume || 500,
        });

        const offsetMin = getDeviceOffsetMin(offset);
        const d = dayjs().utcOffset(offsetMin);
        const nowFake = new Date(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second());
        const startFake = new Date(nowFake);
        startFake.setHours(0, 0, 0, 0);
        const endFake = new Date(nowFake);
        endFake.setHours(23, 59, 59, 999);

        setStartDate(startFake);
        setEndDate(endFake);
        setRecalcStartDate(startFake);
        setRecalcEndDate(endFake);
        setFormatDate(nowFake);
        setBaseDate(nowFake);
        setNewException((p) => ({ ...p, startDate: nowFake, endDate: nowFake }));
        setNewMaterial((p) => ({ ...p, startDate: nowFake, endDate: nowFake }));
        setDatesInitialized(true);
      } catch (err) {
        toast.error("Ошибка загрузки данных устройства");
      }
    };
    loadConfig();
  }, [cameraId]);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset === "custom") return;

    const offsetMin = getDeviceOffsetMin(schedule.timezone_offset);
    const d = dayjs().utcOffset(offsetMin);
    const nowFake = new Date(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second());

    let newStart = new Date(nowFake);
    let newEnd = new Date(nowFake);
    newEnd.setHours(23, 59, 59, 999);

    switch (preset) {
      case "today":
        newStart.setHours(0, 0, 0, 0);
        break;
      case "yesterday":
        newStart.setDate(newStart.getDate() - 1);
        newStart.setHours(0, 0, 0, 0);
        newEnd = new Date(newStart);
        newEnd.setHours(23, 59, 59, 999);
        break;
      case "last_2_days":
        newStart.setDate(newStart.getDate() - 1);
        newStart.setHours(0, 0, 0, 0);
        break;
      case "last_3_days":
        newStart.setDate(newStart.getDate() - 2);
        newStart.setHours(0, 0, 0, 0);
        break;
      case "last_7_days":
        newStart.setDate(newStart.getDate() - 6);
        newStart.setHours(0, 0, 0, 0);
        break;
      case "last_14_days":
        newStart.setDate(newStart.getDate() - 13);
        newStart.setHours(0, 0, 0, 0);
        break;
      case "last_month":
        newStart.setMonth(newStart.getMonth() - 1);
        newStart.setHours(0, 0, 0, 0);
        break;
      default:
        return;
    }
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const handleExportVideo = async () => {
    if (!exportStart || !exportEnd) {
      toast.warn("Выберите даты начала и окончания.");
      return;
    }
    const diffHours = dayjs(exportEnd).diff(dayjs(exportStart), "hour");
    if (diffHours > 120) {
      toast.warn("Диапазон не должен превышать 5 суток (120 часов)!");
      return;
    }
    if (exportStart >= exportEnd) {
      toast.warn("Дата окончания должна быть позже даты начала.");
      return;
    }

    setIsExporting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `/api/devices/${cameraId}/export-video/`,
        {
          start_date: fromFakeLocalToUtcIso(exportStart, schedule.timezone_offset),
          end_date: fromFakeLocalToUtcIso(exportEnd, schedule.timezone_offset),
        },
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "video/mp4" });
      saveAs(blob, `Export_Video_${dayjs().format("YYYYMMDD_HHmm")}.mp4`);
      toast.success("Видео успешно склеено и загружено!");
      setExportModalVisible(false);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.type === "application/json") {
        const reader = new FileReader();
        reader.onload = () => {
          toast.error(JSON.parse(reader.result).error || "Ошибка при экспорте видео");
        };
        reader.readAsText(err.response.data);
      } else {
        toast.error("Ошибка сети или сбой на сервере при склейке видео.");
      }
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (!datesInitialized) return;
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const fetchFolders = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/folders/`, { headers });
        setFolders(res.data);
      } catch (err) {
        toast.error("Ошибка загрузки архива");
      } finally {
        setLoadingFolders(false);
      }
    };
    const fetchAvailableDates = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/available-dates/`, { headers });
        setAvailableDates(res.data.map((d) => new Date(d)));
      } catch (err) {
        toast.error("Ошибка загрузки доступных дат");
      }
    };
    const fetchExceptions = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/schedule-exceptions/`, { headers });
        setExceptions(Array.isArray(res.data) ? res.data : res.data.results || []);
      } catch (err) {
        toast.error("Ошибка загрузки исключений");
      }
    };
    const fetchBreaks = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/breaks/`, { headers });
        setBreaks(Array.isArray(res.data) ? res.data : res.data.results || []);
      } catch (err) {
        toast.error("Ошибка загрузки перерывов");
      }
    };
    const fetchShifts = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/shifts/`, { headers });
        setShifts(Array.isArray(res.data) ? res.data : res.data.results || []);
      } catch (err) {
        toast.error("Ошибка загрузки смен");
      }
    };
    const fetchMaterials = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/materials/`, { headers });
        setMaterials(Array.isArray(res.data) ? res.data : res.data.results || []);
      } catch (err) {
        toast.error("Ошибка загрузки материалов");
      }
    };

    fetchFolders();
    fetchAvailableDates();
    fetchExceptions();
    fetchBreaks();
    fetchShifts();
    fetchMaterials();
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datesInitialized, cameraId]);

  const shiftOptions = useMemo(() => {
    if (schedule.has_multiple_shifts) {
      return shifts.map((s) => ({ id: s.id.toString(), name: s.name, start: s.start_time, end: s.end_time }));
    }
    return [
      {
        id: "default",
        name: t("camera_settings.default_shift_day", "Основная смена"),
        start: schedule.shift_start,
        end: schedule.shift_end,
      },
    ];
  }, [schedule, shifts, t]);

  useEffect(() => {
    if (filterMode === "shift" && selectedShiftId && baseDate) {
      const shift = shiftOptions.find((s) => s.id === selectedShiftId);
      if (shift) {
        const startArr = (shift.start || "00:00").split(":");
        const endArr = (shift.end || "23:59").split(":");
        const newStart = new Date(baseDate);
        newStart.setHours(parseInt(startArr[0], 10), parseInt(startArr[1], 10), 0, 0);
        const newEnd = new Date(baseDate);
        newEnd.setHours(parseInt(endArr[0], 10), parseInt(endArr[1], 10), 59, 999);
        if (newEnd <= newStart) newEnd.setDate(newEnd.getDate() + 1);
        setStartDate(newStart);
        setEndDate(newEnd);
      }
    }
  }, [filterMode, selectedShiftId, baseDate, shiftOptions]);

  const fetchAnalytics = async () => {
    if (!datesInitialized || !startDate || !endDate) return;
    if (analyticsCooldown > 0) {
      toast.warn(`Подождите ${analyticsCooldown} сек. перед повторным запросом`);
      return;
    }
    setLoadingAnalytics(true);
    setAnalyticsData(null);
    setTrajectoryData([]);
    setMapTripId(null);
    try {
      const token = localStorage.getItem("token");
      const params = {
        device_id: cameraId,
        start_date: fromFakeLocalToUtcIso(startDate, schedule.timezone_offset),
        end_date: fromFakeLocalToUtcIso(endDate, schedule.timezone_offset),
      };
      const response = await axios.get("/api/truck-analytics/", {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setAnalyticsData(response.data);
      try {
        // Трек берем за фактически проанализированный период: он сдвинут до целых рейсов
        const trajParams = {
          device_id: cameraId,
          start_date: response.data.date_range?.start || params.start_date,
          end_date: response.data.date_range?.end || params.end_date,
          // точка раз в 10 с: трек не срезает повороты хордами по полкилометра
          step_sec: 10,
          // скорость в каждой точке: раскраска трека и график скорости рейса
          with_speed: 1,
        };
        const trajResponse = await axios.get(`/api/devices/${cameraId}/trajectory/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: trajParams,
        });
        setTrajectoryData(
          trajResponse.data.map((pt) => ({ ...pt, ts: getFakeLocalDate(pt.ts, schedule.timezone_offset) }))
        );
      } catch (err) {
        console.warn("GPS Trajectory not found");
      }
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setLoadingAnalytics(false);
      if (!isSuperuser) setAnalyticsCooldown(10);
    }
  };

  // Соответствие кадр - время записи. Храним сразу в fake-local мс, как все времена на странице
  useEffect(() => {
    if (!selectedFolderId) {
      setFramesMapping([]);
      return;
    }
    const token = localStorage.getItem("token");
    axios
      .get(`/api/folders/${selectedFolderId}/markup/`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) =>
        setFramesMapping(
          (res.data.frames_mapping || [])
            .map((m) => ({ f: m.f, fake: getFakeLocalDate(m.t, schedule.timezone_offset).getTime() }))
            .sort((a, b) => a.f - b.f)
        )
      )
      .catch((err) => {
        if (err.response?.status !== 403) console.warn("Markup data not available:", err.message);
      });
  }, [selectedFolderId, schedule.timezone_offset]);

  // Маркер на таймлайне ведет видео: время текущего кадра по frames_mapping.
  // Видео двигает маркер, но само себя не перематывает.
  const handleVideoProgress = (progressData) => {
    if (performance.now() - lastUserSeekAtRef.current < SEEK_SETTLE_MS) return;
    const vSec = typeof progressData === "number" ? progressData : progressData?.playedSeconds || 0;
    const mapping = framesMappingRef.current;

    let fakeMs = null;
    if (mapping.length) {
      fakeMs = fakeMsAtFrame(mapping, vSec * VIDEO_FPS);
    } else if (currentFolderStartTime) {
      // Пока разметка кадров не загрузилась - по началу часового архива
      const realMs = currentFolderStartTime.valueOf() + vSec * 1000;
      fakeMs = getFakeLocalDate(new Date(realMs).toISOString(), schedule.timezone_offset).getTime();
    }
    if (fakeMs === null) return;
    // Только хранилище: маркер таймлайна и отметка на карте обновятся сами, страница не перерисуется
    playheadStore.set(fakeMs);
  };

  // Сброс масштаба таймлайна на весь период при новых данных
  const dateRangeStartMs = analyticsData?.date_range?.start;
  const dateRangeEndMs = analyticsData?.date_range?.end;
  useEffect(() => {
    if (!dateRangeStartMs || !dateRangeEndMs) {
      viewStore.set(null);
      return;
    }
    viewStore.set({
      start: getFakeLocalDate(dateRangeStartMs, schedule.timezone_offset).getTime(),
      end: getFakeLocalDate(dateRangeEndMs, schedule.timezone_offset).getTime(),
    });
  }, [dateRangeStartMs, dateRangeEndMs, schedule.timezone_offset, viewStore]);

  useEffect(() => {
    if (activeTab === "live" && schedule.realtime_stream_enabled) {
      const fetchLive = async () => {
        try {
          const token = localStorage.getItem("token");
          const res = await axios.get(`/api/devices/${cameraId}/realtime-data/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setLiveData(res.data);
        } catch (e) {
          console.error("Ошибка загрузки живых данных", e);
        }
      };
      fetchLive();
      const id = setInterval(fetchLive, 5000);
      setLiveIntervalId(id);
      return () => clearInterval(id);
    }
    if (liveIntervalId) {
      clearInterval(liveIntervalId);
      setLiveIntervalId(null);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, schedule.realtime_stream_enabled, cameraId]);

  const handleVideoSelect = (folder) => {
    if (!folder.video_file) {
      toast.warn("Видеофайл еще не готов");
      return;
    }
    const startTimeUtc = dayjs(folder.process_after).subtract(1, "hour");
    setCurrentFolderStartTime(startTimeUtc);
    setSelectedVideo(folder.video_file);
    setSelectedFolderId(folder.id);
    lastUserSeekAtRef.current = performance.now();
    setSeekToTime(0);
    setSeekTriggerId((n) => n + 1);
    setCrossVideo(null);
    setVideoModalVisible(true);
  };

  const handleVideoEnded = () => {
    if (!folders || folders.length === 0 || !selectedFolderId) return;

    const validFolders = [...folders]
      .filter((f) => Boolean(f.video_file))
      .sort((a, b) => new Date(a.process_after) - new Date(b.process_after));

    const currentIndex = validFolders.findIndex((f) => f.id === selectedFolderId);

    if (currentIndex !== -1 && currentIndex + 1 < validFolders.length) {
      toast.info("Воспроизведение следующего архива...");
      handleVideoSelect(validFolders[currentIndex + 1]);
    } else {
      toast.info("Вы досмотрели последнее доступное видео в архиве");
    }
  };

  const [formatActionTab, setFormatActionTab] = useState("hide");
  const [formatStats, setFormatStats] = useState(null);

  const openFormatModal = async () => {
    setFormatActionTab("hide");
    setFormatConfirmation("");
    setFormatModalVisible(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/devices/${cameraId}/clear-data/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormatStats(res.data);
    } catch (e) {
      console.warn("Не удалось загрузить статус скрытых папок", e);
    }
  };

  const handleTimeDoubleClick = async ({ exactTime, item, fromSessions = false }) => {
    if (!exactTime) return;
    const fakeDate = new Date(exactTime);
    const utcIsoString = fromFakeLocalToUtcIso(fakeDate, schedule.timezone_offset);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`/api/devices/${cameraId}/video-frame/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          timestamp: utcIsoString,
          include_cross_video: "true",
        },
      });
      const { video_url, seek_seconds, folder_id, cross_video } = response.data;

      if (video_url !== selectedVideo) {
        setFramesMapping([]);
        const folder = folders.find((f) => f.id === folder_id);
        if (folder) {
          const startTimeUtc = dayjs(folder.process_after).subtract(1, "hour");
          setCurrentFolderStartTime(startTimeUtc);
        }
        setSelectedVideo(video_url);
        setSelectedFolderId(folder_id);
      }
      lastUserSeekAtRef.current = performance.now();
      setSeekToTime(seek_seconds);
      setSeekTriggerId((n) => n + 1);
      playheadStore.set(fakeDate.getTime());
      
      setCrossVideo(cross_video || null);

      if (fromSessions) {
        setReturnToSessionsModal(true);
      } else {
        setReturnToSessionsModal(false);
      }

      setVideoModalVisible(true);
      if (customPlayerRef.current) customPlayerRef.current.play();
      toast.info(item ? `Перемотка: ${item.label}` : "Перемотка на выбранное время");
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось найти видео для этого момента.");
      if (fromSessions) {
        setSessionsModalVisible(true);
      }
    }
  };

  // Перемотка из таймлайна, таблиц и графиков. exactTime - fake-local мс.
  const seekImplRef = useRef(null);
  seekImplRef.current = ({ exactTime, item }) => {
    if (!exactTime) return;

    if (crossVideo) {
      handleTimeDoubleClick({ exactTime, item });
      return;
    }

    const mapping = framesMappingRef.current;
    const inCurrentVideo = selectedVideo && mapping.length && exactTime >= mapping[0].fake && exactTime <= mapping[mapping.length - 1].fake;

    if (!inCurrentVideo) {
      handleTimeDoubleClick({ exactTime, item });
      return;
    }

    lastUserSeekAtRef.current = performance.now();
    playheadStore.set(exactTime);
    setSeekToTime(frameAtFakeMs(mapping, exactTime) / VIDEO_FPS);
    setSeekTriggerId((n) => n + 1);
    setVideoModalVisible(true);
  };
  const handleSeek = useCallback((args) => seekImplRef.current(args), []);

  const handleRecalculateSubmit = async () => {
    if (!recalcStartDate || !recalcEndDate || recalcEndDate <= recalcStartDate) {
      toast.warn("Конец периода должен быть позже начала");
      return;
    }
    setRecalculating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/truck-analytics/recalculate/",
        {
          device_id: cameraId,
          start_date: fromFakeLocalToUtcIso(recalcStartDate, schedule.timezone_offset),
          end_date: fromFakeLocalToUtcIso(recalcEndDate, schedule.timezone_offset),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Пересчет запущен");
      setRecalcModalVisible(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка при запуске пересчета");
    } finally {
      setRecalculating(false);
    }
  };

  const handleUploadArchive = async () => {
    if (!uploadFile) return;
    setIsUploading(true);
    setIsProcessing(false);
    setUploadProgress(0);
    setUploadReport({ imported: [], skipped: [], errors: [] });
    const formData = new FormData();
    formData.append("file", uploadFile);
    const token = localStorage.getItem("token");
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/devices/${cameraId}/upload-archive/`, true);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(percent);
        if (percent === 100) {
          setIsUploading(false);
          setIsProcessing(true);
        }
      }
    };
    let lastProcessedIndex = 0;
    let buffer = "";
    xhr.onprogress = () => {
      const currResponse = xhr.responseText;
      const newChunk = currResponse.substring(lastProcessedIndex);
      lastProcessedIndex = currResponse.length;
      buffer += newChunk;
      const lines = buffer.split("\n");
      buffer = lines.pop();
      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const data = JSON.parse(line);
          if (data.status === "error") {
            toast.error(data.message);
          } else if (data.status === "item_imported") {
            setUploadReport((prev) => ({ ...prev, imported: [...(prev?.imported || []), data.file] }));
          } else if (data.status === "item_skipped") {
            setUploadReport((prev) => ({
              ...prev,
              skipped: [...(prev?.skipped || []), `${data.file} (${data.reason})`],
            }));
          } else if (data.status === "item_error") {
            setUploadReport((prev) => ({
              ...prev,
              errors: [...(prev?.errors || []), `${data.file}: ${data.reason}`],
            }));
          } else if (data.status === "finished") {
            toast.success("Обработка архива завершена");
            setTimeout(() => window.location.reload(), 2500);
          }
        } catch (e) {
          console.error("Ошибка парсинга строки:", line, e);
        }
      }
    };
    xhr.onload = () => {
      if (xhr.status !== 200) {
        let errMsg = "Ошибка сервера";
        try {
          errMsg = JSON.parse(xhr.responseText).error || errMsg;
        } catch (e) {
          /* ignore */
        }
        toast.error(errMsg);
        setIsProcessing(false);
      }
    };
    xhr.onerror = () => {
      toast.error("Ошибка сети");
      setIsUploading(false);
      setIsProcessing(false);
    };
    xhr.send(formData);
  };

  const handleClearData = async () => {
    if (!device) return;
    setFormatting(true);
    try {
      const token = localStorage.getItem("token");
      const payload = { action: "hide", mode: formatMode };
      if (formatMode === "date") {
        payload.date = fromFakeLocalToUtcIso(formatDate, schedule.timezone_offset);
      }
      const response = await axios.post(`/api/devices/${cameraId}/clear-data/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message || "Данные успешно скрыты");
      setFormatModalVisible(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка при скрытии данных");
    } finally {
      setFormatting(false);
    }
  };

  const handleRestoreData = async () => {
    if (!device) return;
    setFormatting(true);
    try {
      const token = localStorage.getItem("token");
      const payload = { action: "restore", mode: formatMode };
      if (formatMode === "date") {
        payload.date = fromFakeLocalToUtcIso(formatDate, schedule.timezone_offset);
      }
      const response = await axios.post(`/api/devices/${cameraId}/clear-data/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message || "Видимость успешно восстановлена");
      setFormatModalVisible(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка при восстановлении данных");
    } finally {
      setFormatting(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    setSavingSchedule(true);
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `/api/devices/${cameraId}/`,
        {
          shift_start: schedule.shift_start,
          shift_end: schedule.shift_end,
          work_days: schedule.work_days,
          timezone_offset: parseInt(schedule.timezone_offset, 10) || 0,
          has_multiple_shifts: schedule.has_multiple_shifts,
          include_breaks_in_stats: schedule.include_breaks_in_stats,
          realtime_stream_enabled: schedule.realtime_stream_enabled,
          bucket_volume: parseFloat(schedule.bucket_volume) || 0,
          downtime_threshold_sec: parseInt(schedule.downtime_threshold_sec, 10) || 300,
          fuel_tank_volume: parseInt(schedule.fuel_tank_volume, 10) || 500,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("camera.settings_saved_success"));
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleRealtimeToggle = async (e) => {
    if (!isStaff) return;
    const newValue = e.target.checked;
    setSchedule((prev) => ({ ...prev, realtime_stream_enabled: newValue }));
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `/api/devices/${cameraId}/`,
        { realtime_stream_enabled: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newValue ? "Live трансляция включена" : "Live трансляция отключена");
    } catch (err) {
      setSchedule((prev) => ({ ...prev, realtime_stream_enabled: !newValue }));
      toast.error("Ошибка при переключении Live режима");
    }
  };

  const handleScheduleChange = (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSchedule((prev) => ({ ...prev, [e.target.name]: val }));
  };
  const handleWorkDayChange = (dayIndex) => {
    setSchedule((prev) => ({ ...prev, work_days: prev.work_days ^ (1 << dayIndex) }));
  };

  const handleAddException = async () => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const data = {
        ...newException,
        date: dayjs(newException.startDate).format("YYYY-MM-DD"),
        end_date: newException.endDate
          ? dayjs(newException.endDate).format("YYYY-MM-DD")
          : dayjs(newException.startDate).format("YYYY-MM-DD"),
        custom_shift_start: newException.custom_shift_start || null,
        custom_shift_end: newException.custom_shift_end || null,
      };
      const res = await axios.post(`/api/devices/${cameraId}/schedule-exceptions/`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExceptions((prev) => [...prev, res.data].sort((a, b) => new Date(a.date) - new Date(b.date)));
      const nowFake = getFakeLocalDate(null, schedule.timezone_offset);
      setNewException({
        startDate: nowFake,
        endDate: nowFake,
        is_working: false,
        is_maintenance: false,
        description: "",
        custom_shift_start: "",
        custom_shift_end: "",
      });
      toast.success("Исключение добавлено");
    } catch (err) {
      toast.error(t("common.error"));
    }
  };
  const handleDeleteException = async (id) => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/devices/${cameraId}/schedule-exceptions/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExceptions((prev) => prev.filter((ex) => ex.id !== id));
      toast.success("Исключение удалено");
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const handleAddBreak = async () => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`/api/devices/${cameraId}/breaks/`, newBreak, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBreaks((prev) => [...prev, res.data].sort((a, b) => a.start_time.localeCompare(b.start_time)));
      setNewBreak({ start_time: "", end_time: "", description: "" });
      toast.success("Перерыв добавлен");
    } catch (err) {
      toast.error(t("common.error"));
    }
  };
  const handleDeleteBreak = async (id) => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/devices/${cameraId}/breaks/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBreaks((prev) => prev.filter((b) => b.id !== id));
      toast.success("Перерыв удален");
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const handleAddShift = async () => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`/api/devices/${cameraId}/shifts/`, newShift, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShifts((prev) => [...prev, res.data].sort((a, b) => a.start_time.localeCompare(b.start_time)));
      toast.success("Смена добавлена");
    } catch (err) {
      toast.error(t("common.error"));
    }
  };
  const handleDeleteShift = async (id) => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/devices/${cameraId}/shifts/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShifts((prev) => prev.filter((s) => s.id !== id));
      toast.success("Смена удалена");
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const handleAddMaterial = async () => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const data = {
        ...newMaterial,
        start_date: newMaterial.is_default ? null : dayjs(newMaterial.startDate).format("YYYY-MM-DD"),
        end_date: newMaterial.is_default
          ? null
          : newMaterial.endDate
            ? dayjs(newMaterial.endDate).format("YYYY-MM-DD")
            : dayjs(newMaterial.startDate).format("YYYY-MM-DD"),
      };
      const res = await axios.post(`/api/devices/${cameraId}/materials/`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMaterials((prev) => [...prev, res.data]);
      toast.success("Материал сохранен");
    } catch (err) {
      toast.error(t("common.error"));
    }
  };
  const handleDeleteMaterial = async (id) => {
    if (!isStaff) {
      toast.warn(t("common.role_restricted"));
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/devices/${cameraId}/materials/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMaterials((prev) => prev.filter((m) => m.id !== id));
      toast.success("Материал удален");
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0]);
    setUploadReport(null);
    setUploadProgress(0);
    setIsUploading(false);
    setIsProcessing(false);
  };

  // === ДАННЫЕ ДЛЯ ТАЙМЛАЙНА ===

  const timelineDataForVis = useMemo(() => {
    if (!analyticsData || !analyticsData.timeline || !analyticsData.date_range) return [];

    const result = [];
    const rangeStartFake = getFakeLocalDate(analyticsData.date_range.start, schedule.timezone_offset);
    const rangeEndFake = getFakeLocalDate(analyticsData.date_range.end, schedule.timezone_offset);
    let currentTime = rangeStartFake.getTime();

    const normalized = analyticsData.timeline.map((item) => ({
      ...item,
      class_id: normalizeClassId(item.class_id),
    }));

    const baseTimeline = normalized
      .filter((item) => item.class_id !== -2 && item.class_id !== -3)
      .sort((a, b) => new Date(a.start) - new Date(b.start));

    const overlays = normalized.filter((item) => item.class_id === -2 || item.class_id === -3);

    baseTimeline.forEach((item, index) => {
      const fakeStart = getFakeLocalDate(item.start, schedule.timezone_offset);
      let fakeEnd = getFakeLocalDate(item.end, schedule.timezone_offset);

      if (fakeEnd.getTime() === fakeStart.getTime()) {
        fakeEnd = new Date(fakeEnd.getTime() + 1000);
      }

      let startTimeMs = fakeStart.getTime();
      let endTimeMs = fakeEnd.getTime();

      if (startTimeMs < currentTime) startTimeMs = currentTime;
      // Щель короче SMALL_GAP_MS - стык архивов, а не дыра в данных: сегмент начинаем вплотную
      if (startTimeMs > currentTime && startTimeMs - currentTime < SMALL_GAP_MS && result.length) {
        startTimeMs = currentTime;
      }
      if (endTimeMs <= startTimeMs) endTimeMs = startTimeMs + 1000;

      // Тот же класс вплотную к предыдущему сегменту - один сегмент (разрез на стыке часа/получаса)
      const prevItem = result[result.length - 1];
      if (
        prevItem &&
        prevItem.originalItem?.class_id === item.class_id &&
        item.class_id !== -1 &&
        startTimeMs <= prevItem.end
      ) {
        prevItem.end = endTimeMs;
        currentTime = endTimeMs;
        return;
      }

      // Дыра в данных: устройство не писало IMU
      if (startTimeMs > currentTime) {
        result.push({
          id: `offline_gap_${index}`,
          start: currentTime,
          end: startTimeMs,
          color: classIdToMeta["-1"].color,
          label: classIdToMeta["-1"].label,
          originalItem: { class_id: -1 },
        });
      }

      const meta = classIdToMeta[item.class_id] || classIdToMeta.default;
      result.push({
        id: index,
        start: startTimeMs,
        end: endTimeMs,
        color: meta.color,
        label: meta.label,
        originalItem: item,
      });

      currentTime = endTimeMs;
    });

    const rangeEndMs = rangeEndFake.getTime();
    if (currentTime < rangeEndMs) {
      result.push({
        id: "offline_gap_end",
        start: currentTime,
        end: rangeEndMs,
        color: classIdToMeta["-1"].color,
        label: classIdToMeta["-1"].label,
        originalItem: { class_id: -1 },
      });
    }

    overlays.forEach((item, idx) => {
      const meta = classIdToMeta[item.class_id] || classIdToMeta.default;
      result.push({
        id: `overlay_${idx}`,
        start: getFakeLocalDate(item.start, schedule.timezone_offset).getTime(),
        end: getFakeLocalDate(item.end, schedule.timezone_offset).getTime(),
        color: meta.color,
        label: meta.label,
        originalItem: item,
      });
    });

    return result;
  }, [analyticsData, schedule.timezone_offset, classIdToMeta]);

  const timelineLoadingsForVis = useMemo(() => {
    if (!analyticsData?.loadings) return [];
    return analyticsData.loadings.map((loading, idx) => {
      const start = getFakeLocalDate(loading.start, schedule.timezone_offset).getTime();
      let end = getFakeLocalDate(loading.end, schedule.timezone_offset).getTime();
      if (end <= start) end = start + 5000;
      return {
        id: `loading_${idx}`,
        start,
        end,
        color: classIdToMeta[ACT.LOAD].color,
        label: `Погрузка · ${loading.buckets} ковш.`,
        originalItem: loading,
      };
    });
  }, [analyticsData, schedule.timezone_offset, classIdToMeta]);

  const timelineTripsForVis = useMemo(() => {
    if (!analyticsData?.trips) return [];
    return analyticsData.trips.map((trip, idx) => {
      // рейс: от конца предыдущей разгрузки до конца своей
      const start = getFakeLocalDate(trip.start, schedule.timezone_offset).getTime();
      let end = getFakeLocalDate(trip.end, schedule.timezone_offset).getTime();
      if (end <= start) end = start + 30000;

      const status = trip.loading_detected ? "" : " · без погрузки";
      const imuIcon = trip.determination_method === 'imu' ? " 🚀" : "";
      const excInfo = trip.excavator_name ? ` · 🚜 ${trip.excavator_name}${imuIcon}` : "";

      return {
        id: `trip_${trip.id}`,
        start,
        end,
        // соседние рейсы чередуются двумя оттенками - видно, где кончается один и начинается другой
        color: tripColor(trip, idx),
        label: `Рейс ${trip.id} · ${trip.buckets} ковш.${status}${excInfo}`,
        originalItem: trip,
      };
    });
  }, [analyticsData, schedule.timezone_offset]);

  const dateRangeForVis = useMemo(() => {
    if (!analyticsData || !analyticsData.date_range) return null;
    return {
      start: getFakeLocalDate(analyticsData.date_range.start, schedule.timezone_offset),
      end: getFakeLocalDate(analyticsData.date_range.end, schedule.timezone_offset),
    };
  }, [analyticsData, schedule.timezone_offset]);

  // === ДАННЫЕ ДЛЯ КАРТЫ ===
  const trackIndex = useMemo(() => buildTrackIndex(trajectoryData), [trajectoryData]);
  const mapData = useMemo(() => {
    if (!analyticsData) return { track: [], loadPoints: [], unloadPoints: [], caption: "" };
    const fake = (iso) => getFakeLocalDate(iso, schedule.timezone_offset).getTime();
    const trip = mapTripId != null ? (analyticsData.trips || []).find((tr) => tr.id === mapTripId) : null;
    const from = trip ? fake(trip.start) : -Infinity;
    const to = trip ? fake(trip.end) : Infinity;

    const track = trip
      ? trajectoryData.filter((pt) => {
          const ms = new Date(pt.ts).getTime();
          return ms >= from && ms <= to;
        })
      : trajectoryData;

    const loadPoints = [];
    (analyticsData.loadings || []).forEach((loading, idx) => {
      const start = fake(loading.start);
      const mid = (start + fake(loading.end)) / 2;
      if (mid < from || mid > to) return;
      const coords = positionAt(trackIndex, mid);
      if (!coords) return;
      loadPoints.push({
        id: `load_${idx}`,
        coords,
        timeMs: start,
        hint: `Погрузка · ${loading.buckets} ковш. · ${dayjs(start).format("DD.MM HH:mm")}`,
      });
    });

    const unloadPoints = [];
    (analyticsData.trips || []).forEach((tr) => {
      if ((trip && tr.id !== trip.id) || !tr.unload_start) return;
      const start = fake(tr.unload_start);
      const mid = (start + fake(tr.unload_end || tr.unload_start)) / 2;
      const coords = positionAt(trackIndex, mid);
      if (!coords) return;
      unloadPoints.push({
        id: `unload_${tr.id}`,
        coords,
        timeMs: start,
        hint: `Разгрузка · рейс ${tr.id} · ${dayjs(start).format("DD.MM HH:mm")}`,
      });
    });

    const caption = trip
      ? `Рейс ${trip.id} · ${dayjs(from).format("HH:mm")}–${dayjs(to).format("HH:mm")} · ${trip.buckets} ковш.`
      : "";
    return { track, loadPoints, unloadPoints, caption };
  }, [analyticsData, trajectoryData, trackIndex, mapTripId, schedule.timezone_offset]);

  const showAllTrips = useCallback(() => setMapTripId(null), []);

  // === РЕЙС ПО ВИДЕО ===
  const tripSpans = useMemo(() => {
    if (!analyticsData?.trips) return [];
    return analyticsData.trips
      .map((tr) => ({
        id: tr.id,
        start: getFakeLocalDate(tr.start, schedule.timezone_offset).getTime(),
        end: getFakeLocalDate(tr.end, schedule.timezone_offset).getTime(),
      }))
      .sort((a, b) => a.start - b.start);
  }, [analyticsData, schedule.timezone_offset]);
  const tripSpansRef = useRef(tripSpans);
  tripSpansRef.current = tripSpans;

  const lastVideoTripRef = useRef(undefined);
  const syncTripWithVideo = useCallback(() => {
    if (!autoTripRef.current) return;
    const ms = playheadStore.get();
    if (ms == null) return;
    const spans = tripSpansRef.current;
    let lo = 0;
    let hi = spans.length - 1;
    let found = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (spans[mid].start <= ms) {
        found = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    const tripId = found >= 0 && ms < spans[found].end ? spans[found].id : null;
    if (tripId === lastVideoTripRef.current) return;
    lastVideoTripRef.current = tripId;
    if (tripId !== mapTripIdRef.current) {
      mapTripIdRef.current = tripId;
      setMapTripId(tripId);
    }
  }, [playheadStore]);

  useEffect(() => playheadStore.subscribe(syncTripWithVideo), [playheadStore, syncTripWithVideo]);
  useEffect(() => {
    if (!autoTripFromVideo) return;
    lastVideoTripRef.current = undefined;
    syncTripWithVideo();
  }, [autoTripFromVideo, tripSpans, syncTripWithVideo]);

  const selectTripManually = useCallback((tripId) => {
    setMapTripId(tripId);
  }, []);
  const showAllTripsManually = useCallback(() => {
    setMapTripId(null);
  }, []);

  // === ТАЙМЛАЙНЫ ===
  const timelineProps = {
    t,
    dateRangeForVis,
    timelineDataForVis,
    timelineLoadingsForVis,
    timelineTripsForVis,
    viewStore,
    playheadStore,
    onSeek: handleSeek,
    selectedTripId: mapTripId,
    onTripSelect: setMapTripId,
  };
  const pageTimeline = useMemo(
    () => <TruckTimelineSection {...timelineProps} showPlayhead={false} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, dateRangeForVis, timelineDataForVis, timelineLoadingsForVis, timelineTripsForVis, mapTripId]
  );

  if (!datesInitialized || loadingFolders || !device) {
    return (
      <div className="text-center p-5">
        <CSpinner />
      </div>
    );
  }

  const cameraTabsProps = {
    variant: "truck",
    activeTab,
    setActiveTab,
    schedule,
    handleScheduleChange,
    handleWorkDayChange,
    savingSchedule,
    handleSaveSchedule,
    shifts,
    newShift,
    setNewShift,
    handleAddShift,
    handleDeleteShift,
    breaks,
    newBreak,
    setNewBreak,
    handleAddBreak,
    handleDeleteBreak,
    exceptions,
    newException,
    setNewException,
    handleAddException,
    handleDeleteException,
    materials,
    newMaterial,
    setNewMaterial,
    handleAddMaterial,
    handleDeleteMaterial,
    trajectoryData: [],
    currentAbsoluteTime: null,
    telemetryData: analyticsData
      ? {
          temp: [
            {
              id: "CPU Temp",
              color: "hsl(0, 70%, 50%)",
              data: analyticsData.telemetry.chart_data.map((p) => ({
                x: getFakeLocalDate(p.ts, schedule.timezone_offset),
                y: p.cpu,
              })),
            },
          ],
          battery: [
            {
              id: "Battery %",
              color: "hsl(120, 70%, 50%)",
              data: analyticsData.telemetry.chart_data.map((p) => ({
                x: getFakeLocalDate(p.ts, schedule.timezone_offset),
                y: p.batt,
              })),
            },
          ],
          signal: [
            {
              id: "Signal (0-4)",
              color: "hsl(240, 70%, 50%)",
              data: analyticsData.telemetry.chart_data.map((p) => ({
                x: getFakeLocalDate(p.ts, schedule.timezone_offset),
                y: p.sig,
              })),
            },
          ],
          summary: analyticsData.telemetry.summary,
        }
      : null,
    analyticsData,
    cameraId,
    liveData,
    isStaff,
    handleRealtimeToggle,
    liveImageModalVisible,
    setLiveImageModalVisible,
  };

  const cameraModalsProps = {
    device,
    isStaff,
    recalcModalVisible,
    setRecalcModalVisible,
    recalcStartDate,
    setRecalcStartDate,
    recalcEndDate,
    setRecalcEndDate,
    handleRecalculateSubmit,
    recalculating,
    viewerModalVisible: false,
    setViewerModalVisible: () => {},
    viewerUrl: "",
    isModelLoading: false,
    setIsModelLoading: () => {},
    uploadModalVisible,
    setUploadModalVisible,
    handleFileChange,
    uploadFile,
    isUploading,
    isProcessing,
    uploadProgress,
    uploadReport,
    handleUploadArchive,
    formatModalVisible,
    setFormatModalVisible,
    formatMode,
    setFormatMode,
    formatDate,
    setFormatDate,
    formatStats,
    formatActionTab, 
    setFormatActionTab,
    formatConfirmation,
    setFormatConfirmation,
    handleClearData,
    handleRestoreData,
    formatting,
    exportModalVisible,
    setExportModalVisible,
    exportStart,
    setExportStart,
    exportEnd,
    setExportEnd,
    handleExportVideo,
    isExporting,
  };

  return (
    <div className="bg-light min-vh-100 p-2 p-md-4">
      <CContainer fluid className="dashboard-centered mobile-p-small">
        <CameraHeader deviceName={device.name} organizationName={device.organization_name} t={t} />

        <CCard className="mb-4 border-0 shadow-sm">
          <CCardHeader className="d-flex flex-wrap justify-content-between align-items-center bg-white border-bottom-0 p-3">
            <CameraFilterPanel
              variant="truck"
              t={t}
              isStaff={isStaff}
              isSuperuser={isSuperuser}
              filterMode={filterMode}
              setFilterMode={setFilterMode}
              datePreset={datePreset}
              handlePresetChange={handlePresetChange}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              availableDates={availableDates}
              currentLang={currentLang}
              baseDate={baseDate}
              setBaseDate={setBaseDate}
              selectedShiftId={selectedShiftId}
              setSelectedShiftId={setSelectedShiftId}
              shiftOptions={shiftOptions}
              fetchAnalytics={fetchAnalytics}
              loadingAnalytics={loadingAnalytics}
              triggerExportExcel={null}
              exportingExcel={false}
              setRecalcStartDate={setRecalcStartDate}
              setRecalcEndDate={setRecalcEndDate}
              setRecalcModalVisible={setRecalcModalVisible}
              setFormatConfirmation={setFormatConfirmation}
              setFormatModalVisible={setFormatModalVisible}
              toast={toast}
              device={device}
              analyticsData={analyticsData}
              analyticsCooldown={analyticsCooldown}
              volumeMode={volumeMode}
              setVolumeMode={setVolumeMode}
              openFormatModal={openFormatModal}
            />
          </CCardHeader>

          <CCardBody>
            {analyticsData && (
              <TruckAnalyticsDashboard
                t={t}
                analyticsData={analyticsData}
                device={device}
                volumeMode={volumeMode}
                timezoneOffset={schedule.timezone_offset}
                formatDuration={formatDuration}
                onTimeJump={handleSeek}
                timelineComponent={pageTimeline}
                mapData={mapData}
                speedTrack={trajectoryData}
                mapViewRef={mapViewRef}
                mapTripId={mapTripId}
                onMapTripChange={setMapTripId}
                onShowAllTrips={showAllTrips}
              />
            )}
          </CCardBody>
        </CCard>

        {/* ПЛАВАЮЩИЕ КНОПКИ СПРАВА СНИЗУ */}
        <div className="floating-controls">
          <button
            className="floating-btn floating-btn-video"
            title="Видеоархив"
            onClick={() => {
              setVideoSideTab("archive");
              setVideoModalVisible(true);
            }}
          >
            <CIcon icon={cilMediaPlay} size="xl" />
          </button>
          <button
            className="floating-btn floating-btn-settings"
            title="Настройки, Телеметрия, Карта"
            onClick={() => setOffcanvasVisible(true)}
          >
            <CIcon icon={cilSettings} size="xl" />
          </button>
        </div>

        {/* ВЫЕЗЖАЮЩАЯ ПАНЕЛЬ НАСТРОЕК */}
        <COffcanvas
          placement="end"
          visible={offcanvasVisible}
          onHide={() => setOffcanvasVisible(false)}
          style={{ width: "30vw", minWidth: "400px", maxWidth: "100%", padding: 0, border: "none" }}
        >
          <COffcanvasBody className="p-0">
            <CameraTabs {...cameraTabsProps} onClose={() => setOffcanvasVisible(false)} />
          </COffcanvasBody>
        </COffcanvas>

        {/* МОДАЛЬНОЕ ОКНО ВИДЕОАРХИВА */}
        <CModal
          alignment="center"
          className="truck-video-modal"
          visible={videoModalVisible}
          onClose={() => {
            setVideoModalVisible(false);
            if (returnToSessionsModal) {
              setSessionsModalVisible(true);
              setReturnToSessionsModal(false);
            }
          }}
          fullscreen
        >
          <CModalHeader>
            <CModalTitle>Видео, карта и шкала работы</CModalTitle>
            <div className="d-none d-xl-flex align-items-center gap-2 ms-auto me-3 small">
              <span className="text-muted">Раскладка:</span>
              <CButton
                size="sm"
                color="secondary"
                variant={videoLayout === "bottom" ? undefined : "outline"}
                onClick={() => changeVideoLayout("bottom")}
                title="Видео и карта рядом, шкала снизу"
              >
                Шкала снизу
              </CButton>
              <CButton
                size="sm"
                color="secondary"
                variant={videoLayout === "side" ? undefined : "outline"}
                onClick={() => changeVideoLayout("side")}
                title="Видео слева во всю высоту, карта и шкала справа - удобно для вертикального видео"
              >
                Видео слева
              </CButton>
            </div>
          </CModalHeader>
          <CModalBody className="bg-light d-flex flex-column gap-3" style={{ minHeight: 0, overflowY: "auto" }}>
            <style>{VIDEO_MODAL_CSS}</style>
            <div className={`truck-modal-main truck-layout-${videoLayout}`}>
              <div className="truck-modal-video">
                <CameraVideoPlayer
                  variant="truck"
                  compact={lowViewport}
                  t={t}
                  selectedVideo={selectedVideo}
                  setExportModalVisible={setExportModalVisible}
                  customPlayerRef={customPlayerRef}
                  seekToTime={seekToTime}
                  seekTriggerId={seekTriggerId}
                  onProgress={handleVideoProgress}
                  onEnded={handleVideoEnded}
                  crossVideo={crossVideo}
                  primaryFramesMapping={framesMapping}
                />
              </div>

              <div className="truck-modal-side">
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <CButton
                    size="sm"
                    color="primary"
                    variant={videoSideTab === "map" ? undefined : "outline"}
                    onClick={() => setVideoSideTab("map")}
                  >
                    Карта
                  </CButton>
                  <CButton
                    size="sm"
                    color="primary"
                    variant={videoSideTab === "archive" ? undefined : "outline"}
                    onClick={() => setVideoSideTab("archive")}
                  >
                    Видеоархив
                  </CButton>
                  {analyticsData && (
                    <TripProgressStrip
                      playheadStore={playheadStore}
                      trips={timelineTripsForVis}
                      timeRange={dateRangeForVis}
                      onSeek={handleSeek}
                      className="flex-grow-1 ms-auto"
                      style={{ maxWidth: "640px" }}
                    />
                  )}
                </div>

                {videoSideTab === "map" ? (
                  <CCard className="border-0 shadow-sm truck-modal-mapbox">
                    <CCardBody className="p-2 d-flex flex-column" style={{ minHeight: 0 }}>
                      {analyticsData ? (
                        <TruckMap
                          fill
                          track={mapData.track}
                          loadPoints={mapData.loadPoints}
                          unloadPoints={mapData.unloadPoints}
                          viewState={mapViewRef}
                          playheadStore={playheadStore}
                          onPointClick={handleSeek}
                          caption={mapData.caption}
                          onShowAll={mapTripId != null ? showAllTripsManually : null}
                          autoTrip={autoTripFromVideo}
                          onAutoTripChange={setAutoTripFromVideo}
                          activities={timelineDataForVis}
                        />
                      ) : (
                        <div className="p-4 text-center text-muted">Получите аналитику, чтобы увидеть трек</div>
                      )}
                    </CCardBody>
                  </CCard>
                ) : (
                  <div className="d-flex truck-modal-mapbox" style={{ overflowY: "auto" }}>
                    <CameraArchiveList
                      t={t}
                      isStaff={isStaff}
                      toast={toast}
                      setUploadModalVisible={setUploadModalVisible}
                      folders={folders}
                      selectedVideo={selectedVideo}
                      handleVideoSelect={handleVideoSelect}
                      timezoneOffset={schedule.timezone_offset}
                    />
                  </div>
                )}

                {analyticsData && videoLayout === "side" && (
                  <div className="flex-shrink-0 truck-modal-timeline">
                    <TruckTimelineSection {...timelineProps} onTripSelect={selectTripManually} showPlayhead compact={lowViewport} />
                  </div>
                )}
              </div>
            </div>

            {analyticsData && videoLayout === "bottom" && (
              <div className="flex-shrink-0 truck-modal-timeline">
                <TruckTimelineSection {...timelineProps} onTripSelect={selectTripManually} showPlayhead compact={lowViewport} />
              </div>
            )}
          </CModalBody>
        </CModal>

        <CameraExtraModals
          sessionsModalVisible={sessionsModalVisible}
          setSessionsModalVisible={setSessionsModalVisible}
          selectedTruckData={{ label: "", sessions: [] }}
          sessionSortBy="time_asc"
          setSessionSortBy={() => {}}
          sortedSessions={[]}
          schedule={schedule}
          setSelectedSessionInfo={() => {}}
          setInfoModalVisible={() => {}}
          handleTimeDoubleClick={handleTimeDoubleClick}
          infoModalVisible={false}
          selectedSessionInfo={null}
          liveImageModalVisible={liveImageModalVisible}
          setLiveImageModalVisible={setLiveImageModalVisible}
          liveData={liveData}
          device={device}
          trucksList={[]}
          refreshAnalytics={fetchAnalytics}
        />
      </CContainer>

      <CameraModals {...cameraModalsProps} />
    </div>
  );
};

export default TruckDetailPage;